// Text objects: fonts, palette, defaults, and the one Konva node description shared by the
// on-screen stage and the export so they can never drift apart.

import FONTS_JSON from '../../shared/fonts.json';
import { ASSET_BASE } from './assets';
import type { TextObject } from './design';
import type { Geometry } from './geometry';

export const FONTS = Object.keys(FONTS_JSON) as (keyof typeof FONTS_JSON)[];

export const PALETTE = [
  '#ffffff', '#000000', '#e53935', '#f48fb1', '#e91e63', '#fb8c00',
  '#fdd835', '#43a047', '#00897b', '#1e88e5', '#8e24aa', '#6d4c41',
];

let injected = false;
/** @font-face must live in the document head — inside a shadow root it is unreliable. */
export function injectFontFaces(): void {
  if (injected) return;
  injected = true;
  const style = document.createElement('style');
  // The UI face (Source Sans 3, published as "Source Sans Pro") rides along; it is not a design font.
  style.textContent = [...FONTS.map((name) => [name, FONTS_JSON[name]] as const), ['Source Sans Pro', 'SourceSans3.woff2'] as const]
    .map(([name, file]) => `@font-face{font-family:"${name}";src:url("${ASSET_BASE}/fonts/${file}") format("woff2");font-display:swap}`)
    .join('\n');
  document.head.appendChild(style);
}

export function loadFonts(names: readonly string[] = FONTS): Promise<unknown> {
  injectFontFaces();
  return Promise.all(names.map((n) => document.fonts.load(`20px "${n}"`))).catch(() => undefined);
}

let counter = 0;
export function newText(g: Geometry, text: string): TextObject {
  return {
    id: `t${Date.now().toString(36)}${counter++}`,
    text, font: FONTS[0], color: '#ffffff', shadow: true,
    fontSizePx: Math.round(Math.min(g.outW, g.outH) * 0.08),
    centerX: g.outW / 2, centerY: g.outH / 2, rotation: 0, align: 'center',
  };
}

/** Konva.Text config at stage scale `s` (1 for export). Centre via offset — see centreNode. */
export function textConfig(t: TextObject, s: number) {
  const size = t.fontSizePx * s;
  return {
    text: t.text, fontFamily: t.font, fontSize: size, fill: t.color, align: t.align, lineHeight: 1.1,
    x: t.centerX * s, y: t.centerY * s, rotation: t.rotation,
    shadowEnabled: t.shadow, shadowColor: 'rgba(0,0,0,0.5)', shadowBlur: size * 0.08,
    shadowOffsetX: size * 0.04, shadowOffsetY: size * 0.04,
  };
}

/** Konva positions text by its top-left; move the origin to the centre so x/y and rotation are about it. */
export function centreNode(node: { width(): number; height(): number; offsetX(v: number): unknown; offsetY(v: number): unknown }): void {
  node.offsetX(node.width() / 2);
  node.offsetY(node.height() / 2);
}
